import { FC, useCallback, useEffect, useMemo } from 'react';

import chassisImage from 'assets/chassis-icon.svg';
import containerImage from 'assets/container-icon.svg';
import gensetImage from 'assets/genset-icon.svg';

import { Dialog, useDialog } from 'components';
import { EquipmentType } from 'types/damage';

import { useDamage } from '.';
import ContainerDialogContent from './container-damage';

import ChassisDialogContent from './chassis-damage';
import './damages.scss';
import GensetDialogContent from './genset-damage';

const Damages: FC<{ [P in EquipmentType]: boolean }> = enabledTypes => {
  const { current, damages, editing, reset, setCurrent } = useDamage();
  const { closeDialog, openDialog, ref } = useDialog({ onClose: reset });

  const counts = useMemo(() => {
    return damages
      .filter(d => !!d)
      .reduce(
        (acc, { equipmentType: equipment }) => {
          return !equipment ? acc : { ...acc, [equipment]: acc[equipment] + 1 };
        },
        { CHASSIS: 0, CONTAINER: 0, GENSET: 0 }
      );
  }, [damages]);

  const DialogContent: FC<{ closeDialog: () => void }> = useMemo(() => {
    switch (current?.equipmentType) {
      case EquipmentType.CONTAINER:
        return ContainerDialogContent;
      case EquipmentType.CHASSIS:
        return ChassisDialogContent;
      case EquipmentType.GENSET:
        return GensetDialogContent;
      default:
        return () => null;
    }
  }, [current?.equipmentType]);

  const handleOpenDialog = useCallback(
    (equipmentType: EquipmentType) => {
      setCurrent({ equipmentType });
      openDialog();
    },
    [openDialog, setCurrent]
  );

  useEffect(() => {
    if (editing >= 0 && !ref.current?.open) {
      openDialog();
    }
  }, [editing, ref, openDialog]);

  return (
    <div id="transaction-damages">
      <div className="grid-3">
        <div className="badge-container h-100 w-100">
          <button
            id="container-damage-button"
            type="button"
            className="btn-outline text-info-main g-2 h-100 w-100"
            onClick={() => handleOpenDialog(EquipmentType.CONTAINER)}
            disabled={!enabledTypes[EquipmentType.CONTAINER]}
          >
            <img alt="container" src={containerImage} />
            <label className="font-weight-bold text-md">CONTAINER</label>
          </button>
          {!!counts.CONTAINER && <div className="badge bg-primary-dark text-md text-white">{counts.CONTAINER}</div>}
        </div>
        <div className="badge-container h-100 w-100">
          <button
            id="chassis-damage-button"
            type="button"
            className="btn-outline text-info-main g-2 h-100 w-100"
            onClick={() => handleOpenDialog(EquipmentType.CHASSIS)}
            disabled={!enabledTypes[EquipmentType.CHASSIS]}
          >
            <img alt="chassis" src={chassisImage} />
            <label className="font-weight-bold text-md">CHASSIS</label>
          </button>
          {!!counts.CHASSIS && <div className="badge bg-primary-dark text-md text-white">{counts.CHASSIS}</div>}
        </div>
        <div className="badge-container h-100 w-100">
          <button
            id="genset-damage-button"
            type="button"
            className="btn-outline text-info-main g-2 h-100 w-100"
            onClick={() => handleOpenDialog(EquipmentType.GENSET)}
            disabled={!enabledTypes[EquipmentType.GENSET]}
          >
            <img alt="genset" src={gensetImage} />
            <label className="font-weight-bold text-md">GENSET</label>
          </button>
          {!!counts.GENSET && <div className="badge bg-primary-dark text-md text-white">{counts.GENSET}</div>}
        </div>
      </div>
      <Dialog closeDialog={closeDialog} ref={ref}>
        <DialogContent closeDialog={closeDialog} />
      </Dialog>
    </div>
  );
};

export default Damages;
